/**
 * Phase 7 Commit J. Contact step — 4 fields, phone country-code prefix,
 * autocomplete + inputmode wired correctly. The form's submit button
 * doubles as the action bar (no RunningBar — Contact's primary action
 * is "submit form" which lives best on the form itself).
 */

import { useState } from 'preact/hooks';
import { ArrowIcon, BackIcon } from '../icons';
import { validatePhone } from '../phone';

interface Props {
  onSubmit: (info: {
    givenName: string;
    familyName?: string;
    email?: string;
    phone: string;
  }) => void;
  submitting: boolean;
  requireEmail?: boolean;
  requirePhone?: boolean;
  prefilled?: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
  };
  onBack?: () => void;
  /**
   * Per-tenant view-bundle flag. 'be-you' suppresses the eyebrow
   * ("Your details") per the med-spa CRO direction. Null = default.
   */
  customViewBundle?: string | null;
  /**
   * Phase 6 — replace the booking copy (a Special Services purchase has
   * no appointment to confirm or remind about). Absent everywhere else,
   * which keeps every other tenant's copy exactly as it is.
   */
  copy?: { title: string; sub: string; fineprint: string };
}

export function ContactStep({
  onSubmit,
  submitting,
  requireEmail = false,
  requirePhone = true,
  prefilled,
  onBack,
  customViewBundle,
  copy,
}: Props) {
  const isBeYou = customViewBundle === 'be-you';
  const [givenName, setGivenName] = useState(prefilled?.firstName ?? '');
  const [familyName, setFamilyName] = useState(prefilled?.lastName ?? '');
  const [email, setEmail] = useState(prefilled?.email ?? '');
  const [phone, setPhone] = useState(prefilled?.phone ?? '');
  const [formError, setFormError] = useState<string | null>(null);

  function handleSubmit(e: Event) {
    e.preventDefault();
    setFormError(null);
    if (!givenName.trim()) {
      setFormError('First name is required.');
      return;
    }
    if (!familyName.trim()) {
      setFormError('Last name is required.');
      return;
    }
    let phoneOut = '';
    if (requirePhone || phone.trim()) {
      // Use the strict validator — Square's CreateBooking 400s on
      // fictitious / structurally-invalid numbers and the previous
      // "10 digits is fine" check let those through to fail silently
      // on the Confirm step.
      const result = validatePhone(phone);
      if (!result.ok) {
        setFormError(result.reason);
        return;
      }
      phoneOut = result.e164;
    }
    if (requireEmail && !email.trim()) {
      setFormError('Email is required.');
      return;
    }
    if (email && !/^\S+@\S+\.\S+$/.test(email)) {
      setFormError('Please enter a valid email address.');
      return;
    }
    onSubmit({
      givenName: givenName.trim(),
      familyName: familyName.trim() || undefined,
      email: email.trim() || undefined,
      phone: phoneOut,
    });
  }

  const isPhdStudio = customViewBundle === 'phd-studio';
  return (
    <form class="sbw-scroll" onSubmit={handleSubmit}>
      {!isBeYou && !isPhdStudio && <div class="sbw-eyebrow">Your details</div>}
      <h2 class="sbw-step-title">
        {copy
          ? copy.title
          : isBeYou
          ? 'Almost booked.'
          : isPhdStudio
            ? 'Reserve your consultation'
            : 'Almost yours.'}
      </h2>
      <p class="sbw-step-sub">
        {copy
          ? copy.sub
          : isBeYou
          ? 'Just your name and number to confirm. We’ll send a reminder text 24 hours before — no spam, ever.'
          : isPhdStudio
            ? 'A few details so we can confirm your consultation slot. Never shared.'
            : 'A name and number so we can confirm. We never share or DM.'}
      </p>

      <div class="sbw-form">
        <div class="sbw-row">
          <div class="sbw-field">
            <span class="sbw-field-label">First name</span>
            <input
              class={'sbw-input' + (givenName ? ' has-value' : '')}
              type="text"
              value={givenName}
              onInput={(e) =>
                setGivenName((e.target as HTMLInputElement).value)
              }
              autocomplete="given-name"
              inputmode="text"
              required
            />
          </div>
          <div class="sbw-field">
            <span class="sbw-field-label">Last name</span>
            <input
              class={'sbw-input' + (familyName ? ' has-value' : '')}
              type="text"
              value={familyName}
              onInput={(e) =>
                setFamilyName((e.target as HTMLInputElement).value)
              }
              autocomplete="family-name"
              inputmode="text"
              required
            />
          </div>
        </div>
        <div class="sbw-field">
          <span class="sbw-field-label">
            Phone {requirePhone ? '' : <span style={{ opacity: 0.5 }}>· optional</span>}
          </span>
          <div class="sbw-phone">
            <span class="sbw-phone-cc">
              🇺🇸 +1
            </span>
            <input
              type="tel"
              value={phone}
              onInput={(e) =>
                setPhone((e.target as HTMLInputElement).value)
              }
              placeholder="(415) 867-5309"
              autocomplete="tel"
              inputmode="numeric"
              required={requirePhone}
            />
          </div>
        </div>
        <div class="sbw-field">
          <span class="sbw-field-label">
            Email{' '}
            {requireEmail ? '' : <span style={{ opacity: 0.5 }}>· optional</span>}
          </span>
          <input
            class={'sbw-input' + (email ? ' has-value' : '')}
            type="email"
            value={email}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
            placeholder="you@email.com"
            autocomplete="email"
            inputmode="email"
            required={requireEmail}
          />
        </div>
        <p class="sbw-fineprint">
          {copy ? (
            copy.fineprint
          ) : (
            <>
              By continuing you agree to a confirmation text and the salon&apos;s
              cancellation policy.
            </>
          )}
        </p>
        {formError && <div class="sbw-error">{formError}</div>}
      </div>

      <div class="sbw-actionbar">
        {onBack && (
          <button
            type="button"
            class="sbw-btn sbw-btn--ghost"
            onClick={onBack}
            disabled={submitting}
            aria-label="Go back"
          >
            <BackIcon />
          </button>
        )}
        <button
          type="submit"
          class="sbw-btn sbw-btn--brand"
          disabled={submitting}
        >
          {submitting ? 'One sec…' : 'Continue'}
          {!submitting && <ArrowIcon />}
        </button>
      </div>
    </form>
  );
}
